import React, { useState } from 'react';
import {
  GiftOutlined,
  LeftOutlined,
  NotificationOutlined,
  TeamOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { usePermissions } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import useOrbitLayer4 from '../../l4/useOrbitLayer4';
import AudienceBuilder from '../audienceBuilder/AudienceBuilder';
import { PlainButton } from '../components/pageStyles';
import HistoryList from '../history/HistoryList';
import { HistoryRow } from '../history/historyModel';
import RewardWizard from '../rewardWizard/RewardWizard';
import RuleWizard from '../ruleWizard/RuleWizard';
import CampaignWizard from '../wizard/CampaignWizard';
import SelectionTile, { TileRow } from '../wizard/components/SelectionTile';
import { WizardEntry } from '../wizard/components/WizardFrame';
import { LinkButton, tint, wz, wzType } from '../wizard/wizardStyles';
import { CampaignWizardSubmit } from '../wizard/wizardSubmission';
import { ConfigEntity, ConfigHandoff } from './configSubmission';
import { TC } from './kit/ConfigPanel';
import ConfigWizardRoot from './kit/ConfigWizardRoot';
import useConfigStudioSaves from './useConfigStudioSaves';

export type ConfigTab = 'config' | 'history';

// 71 / 72: a "Config Studio" heading with the Config / History tab pair over the panel.
const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1180px;
  margin: -40px auto 22px;
`;

const Tabs = styled.div`
  display: inline-flex;
  padding: 3px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 10px;
  background: ${wz.field};
`;

const Tab = styled(PlainButton)<{ $on: boolean }>`
  min-width: 84px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-weight: 700;
  ${wzType.label}
  ${({ $on }) =>
    $on
      ? css`
          background: ${tint(wz.accent, 0.14)};
          color: ${wz.accent};
        `
      : css`
          color: ${wz.secondary};
        `}
`;

const ENTITIES: { entity: ConfigEntity; icon: React.ReactNode; resource: string }[] = [
  { entity: 'campaign', icon: <NotificationOutlined />, resource: 'campaigns' },
  { entity: 'rule', icon: <ThunderboltOutlined />, resource: 'transaction_rules' },
  { entity: 'reward', icon: <GiftOutlined />, resource: 'rewards' },
  { entity: 'audience', icon: <TeamOutlined />, resource: 'audiences' }
];

const DisabledReason = styled.p`
  margin: 12px 0 0;
  color: ${wz.muted};
  ${wzType.hint}
`;

/** Whether the user may create each entity; a disabled tile says why (plan F-e). */
const useCreateRights = (): Record<ConfigEntity, boolean> => {
  const campaigns = usePermissions('campaigns');
  const rules = usePermissions('transaction_rules');
  const rewards = usePermissions('rewards');
  const audiences = usePermissions('audiences');
  return {
    campaign: campaigns.canCreate || campaigns.canEdit,
    rule: rules.canCreate || rules.canEdit,
    reward: rewards.canCreate || rewards.canEdit,
    audience: audiences.canEdit
  };
};

/** Not drawn: the Config tab's first screen, one tile per configurable entity. */
const EntityPicker: React.FC<{ onPick: (entity: ConfigEntity) => void }> = ({ onPick }) => {
  const { t } = useMultiLanguageTranslation();
  const rights = useCreateRights();
  const blocked = ENTITIES.filter(item => !rights[item.entity]);
  return (
    <WizardEntry
      title={t(`${TC}.tabs.label`)}
      step={null}
      onBack={null}
      question={t(`${TC}.entry.title`)}
    >
      <TileRow>
        {ENTITIES.map(item => (
          <span
            key={item.entity}
            style={{ flex: '1 1 0', opacity: rights[item.entity] ? 1 : 0.45 }}
          >
            <SelectionTile
              icon={item.icon}
              label={t(`${TC}.entry.${item.entity}`)}
              selected={false}
              onSelect={() => rights[item.entity] && onPick(item.entity)}
              testId={`orbit-config-entity-${item.entity}`}
            />
          </span>
        ))}
      </TileRow>
      {blocked.length > 0 ? (
        <DisabledReason role="note">
          {t(`${TC}.entry.noPermission`, {
            entities: blocked.map(item => t(`${TC}.entry.${item.entity}`)).join(', ')
          })}
        </DisabledReason>
      ) : null}
    </WizardEntry>
  );
};

interface IConfigStudioProps {
  /** The campaign wizard's save; rule, reward and audience save through the module APIs here. */
  submitCampaign: CampaignWizardSubmit;
  /** Told about every record a flow created (`IBuilderSaveResult`). */
  onHandoff: ConfigHandoff;
  /** Back to the conversation the studio was opened over. */
  onExit: () => void;
  initialTab?: ConfigTab;
  /** The History rows; `HISTORY_FIXTURE` until a history API exists. */
  historyRows: HistoryRow[];
  /** The History copy icon; no duplicate flow is drawn. */
  onDuplicate: (row: HistoryRow) => void;
}

/**
 * The Config Studio host (71, 72): a Config tab with the four flows and a History tab (67).
 * Everything is behind `orbit_layer_4`.
 */
const ConfigStudio: React.FC<IConfigStudioProps> = ({
  submitCampaign,
  onHandoff,
  onExit,
  initialTab = 'config',
  historyRows,
  onDuplicate
}) => {
  const { t } = useMultiLanguageTranslation();
  const enabled = useOrbitLayer4();
  const saves = useConfigStudioSaves();
  const [tab, setTab] = useState<ConfigTab>(initialTab);
  const [flow, setFlow] = useState<ConfigEntity | null>(null);

  if (!enabled) {
    return null;
  }

  const toPicker = () => setFlow(null);
  const renderConfig = () => {
    switch (flow) {
      case 'campaign':
        return <CampaignWizard onSubmit={submitCampaign} onExit={onExit} onHandoff={onHandoff} />;
      case 'rule':
        return <RuleWizard onSubmit={saves.rule} onExit={toPicker} onHandoff={onHandoff} />;
      case 'reward':
        return <RewardWizard onSubmit={saves.reward} onExit={toPicker} onHandoff={onHandoff} />;
      case 'audience':
        return (
          <AudienceBuilder onSubmit={saves.audience} onExit={toPicker} onHandoff={onHandoff} />
        );
      default:
        return (
          <ConfigWizardRoot testId="orbit-config-picker" step="picker" onJump={null}>
            <EntityPicker onPick={setFlow} />
          </ConfigWizardRoot>
        );
    }
  };

  return (
    <div data-testid="orbit-config-studio" data-tab={tab}>
      <Head>
        {/* The campaign wizard draws its own way back to the chat. */}
        {tab === 'config' && flow === 'campaign' ? (
          <span />
        ) : (
          <LinkButton type="button" onClick={onExit} data-testid="orbit-config-exit">
            <LeftOutlined aria-hidden="true" style={{ fontSize: 10 }} />
            <span>{t(`${TC}.exit`)}</span>
          </LinkButton>
        )}
        <Tabs role="tablist" aria-label={t(`${TC}.tabs.label`)}>
          {(['config', 'history'] as ConfigTab[]).map(option => (
            <Tab
              key={option}
              type="button"
              role="tab"
              aria-selected={tab === option}
              $on={tab === option}
              onClick={() => setTab(option)}
            >
              {t(`${TC}.tabs.${option}`)}
            </Tab>
          ))}
        </Tabs>
      </Head>
      <div role="tabpanel" aria-label={t(`${TC}.tabs.${tab}`)}>
        {tab === 'config' ? (
          renderConfig()
        ) : (
          // The dark skin, popup home and scrollbar recipe of the flows, around the list too.
          <ConfigWizardRoot testId="orbit-config-history" step="history" onJump={null}>
            <HistoryList
              rows={historyRows}
              onDuplicate={onDuplicate}
              onNew={() => {
                setFlow(null);
                setTab('config');
              }}
            />
          </ConfigWizardRoot>
        )}
      </div>
    </div>
  );
};

export default ConfigStudio;
